import * as React from 'react';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import { Box, Button, Paper, Typography, Checkbox, Drawer, Fab, Select, MenuItem, ListSubheader, ListItemText, FormControl, InputLabel } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import FiltersBar from '../../components/FiltersBar';
import { useTranslation } from 'react-i18next';
import { ApiContext } from '../../App';
import TrendCharts from './TrendCharts';
import LinearProgress from "@mui/material/LinearProgress";


const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;


export default function Trends(props) {

    const { facility } = props;
    const currencySymbol = props.clientInfo.currency_symbol;
    const localTimeZone = props.clientInfo.timezone;

    const { t } = useTranslation();

    var earlierDate = new Date();
    earlierDate.setDate(earlierDate.getDate() - 1);
    const [fromDate, setFromDate] = React.useState(earlierDate);
    const [toDate, setToDate] = React.useState(new Date());
    const [timeAggType, setTimeAggType] = React.useState("h");
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");

    const [isLoading, setIsLoading] = React.useState(false);

    const [loadedFilters, setLoadedFilters] = React.useState(false);

    const [allTrendsData, setAllTrendsData] = React.useState({
        inputTrends: {},
        energyPriceTrends: {},
        environmentalTrends: {}
    });

    const [filterDrawerOpen, setFilterDrawerOpen] = React.useState(false);

    const [selectedKeys, setSelectedKeys] = React.useState([]);
    const [appliedKeys, setAppliedKeys] = React.useState([]);

    const drawerInputWidth = 500;

    const [filters, setFilters] = React.useState({
        system: [],
        location: []
    });

    const [selectedFilters, setSelectedFilters] = React.useState({
        system: [],
        location: []
    });

    const [appliedFilters, setAppliedFilters] = React.useState({
        system: [],
        location: []
    });

    const { facilityApi } = React.useContext(ApiContext)

    const handleTimeAggTypeChange = (event, newValue) => {
        if (newValue !== null) {
            setTimeAggType(newValue);
        }
    }

    const handleAPIError = () => {
        setAlertMessage(t("Problem fetching data."));
        setAlertOpen(true);
    }

    const { readingsApi } = React.useContext(ApiContext)

    React.useEffect(() => {

        readingsApi.getTrendFilters(
            facility,
            filterData => {

                setFilters(filterData);

                facilityApi.retrieve(
                    facility,
                    facilityInfo => {

                        const savedAppliedFilters = facilityInfo.trends_applied_filters;
                        const savedAppliedKeys = facilityInfo.trends_applied_keys;

                        setSelectedFilters(savedAppliedFilters);
                        setAppliedFilters(savedAppliedFilters);
                        setSelectedKeys(savedAppliedKeys);
                        setAppliedKeys(savedAppliedKeys);

                        setLoadedFilters(true);

                    },
                    () => {
                        handleAPIError();
                    }
                )

            },
            () => {
                handleAPIError();
            }
        )
    },
        [facility]
    )

    React.useEffect(() => {

        if ((toDate - fromDate) >= 31 * 24 * 3600 * 1000 && timeAggType === 'h') {
            setTimeAggType('d')
        } else {
            if (loadedFilters) {

                setIsLoading(true);

                readingsApi.getAllTrends(
                    facility,
                    fromDate,
                    toDate,
                    data => {
                        setAllTrendsData(({
                            inputTrends: data.input_trends,
                            energyPriceTrends: data.energy_price_trends,
                            environmentalTrends: data.environmental_trends
                        }));
                        setIsLoading(false);
                    },
                    () => {
                        handleAPIError();
                        setIsLoading(false);
                    },
                    timeAggType,
                    appliedKeys,
                    appliedFilters?.location.length !== filters.location.length ? appliedFilters.location : [],
                    appliedFilters?.system.length !== filters.system.length ? appliedFilters.system : []
                )
            }
        }

    },
        [fromDate, toDate, timeAggType, appliedFilters, appliedKeys, loadedFilters]
    )

    const closeFilterDrawer = () => {
        setFilterDrawerOpen(false);
        setAppliedFilters(selectedFilters);
    }

    const handleKeyChange = () => {
        setAppliedKeys(selectedKeys);
    }

    const handleSave = () => {
        facilityApi.saveTrendPreferences(
            facility,
            appliedKeys, appliedFilters,
            () => {
                window.alert(t("Preferences saved correctly."))
            },
            handleAPIError
        )
    }

    return (

        <Box marginBottom={5}>
            <Stack>
                <FiltersBar
                    selectedKeys={selectedKeys}
                    setSelectedKeys={setSelectedKeys}
                    onKeyClose={handleKeyChange}
                    fromDate={fromDate}
                    toDate={toDate}
                    onFromDateChange={(newDate) => setFromDate(newDate)}
                    onToDateChange={(newDate) => setToDate(newDate)}
                    timeAggType={timeAggType}
                    onTimeAggChange={handleTimeAggTypeChange}
                    onOpenFilterDrawer={() => setFilterDrawerOpen(true)}
                    onSave={handleSave}
                />
                {isLoading && <Box sx={{ width: '100%' }}>
                    <LinearProgress />
                </Box>}
                <TrendCharts
                    inputData={allTrendsData.inputTrends}
                    environmentalData={allTrendsData.environmentalTrends}
                    energyPriceData={allTrendsData.energyPriceTrends}
                    timeAggType={timeAggType}
                    currencySymbol={currencySymbol}
                    localTimeZone={localTimeZone}
                />
            </Stack>
            <Drawer anchor="right" open={filterDrawerOpen} onClose={() => closeFilterDrawer()}>
                <Stack spacing={3} padding={2}>
                    <Typography variant="h6" color="primary">{t("Filter")}</Typography>
                    <Autocomplete
                        multiple
                        limitTags={5}
                        value={selectedFilters.location}
                        onChange={(event, newValue) => {
                            setSelectedFilters((val) => ({ ...val, location: newValue }))
                        }}
                        options={filters.location}
                        sx={{ width: drawerInputWidth }}
                        renderInput={(params) => <TextField {...params} label={t("Locations")} />}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                {option}
                            </li>
                        )}
                    />
                    <Autocomplete
                        multiple
                        limitTags={5}
                        value={selectedFilters.system}
                        onChange={(event, newValue) => {
                            setSelectedFilters((val) => ({ ...val, system: newValue }))
                        }}
                        options={filters.system}
                        sx={{ width: drawerInputWidth }}
                        renderInput={(params) => <TextField {...params} label={t("Systems")} />}
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                                {option}
                            </li>
                        )}
                    />
                </Stack>
            </Drawer>
            <Dialog
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
            >
                <DialogTitle id="alert-dialog-title">
                    {t("Problem")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {alertMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAlertOpen(false)} autoFocus>
                        {t("OK")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>

    )

};